import React, { useRef, useEffect, useCallback } from 'react';
import Input from './Input';

interface OTPProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  size?: 'xl' | 'lg' | 'md';
  variant?: 'default' | 'outline';
  color?: 'default' | 'success' | 'warning' | 'error' | 'info';
  className?: string;
  inputWrapperclassName?: string;
  inputClassName?: string;
  label?: string;
  helperText?: string;
  helperTextClassName?: string;
  ariaLabel?: string;
  separator?: boolean;
}

const OTP: React.FC<OTPProps> = ({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  autoFocus = true,
  size = 'lg',
  variant = 'default',
  color = 'default',
  className = '',
  inputWrapperclassName = '',
  inputClassName = '',
  label,
  helperText,
  helperTextClassName = '',
  ariaLabel,
  separator = false,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const currentValue = value.padEnd(length, '');

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0] && !disabled) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus, disabled]);

  const handleInputChange = useCallback(
    (index: number, inputValue: string) => {
      const sanitizedValue = inputValue.replace(/[^a-zA-Z0-9]/g, '');

      if (sanitizedValue.length > 1) {
        const pastedValue = sanitizedValue.slice(0, length - index);
        const newValue = currentValue.split('');

        for (let i = 0; i < pastedValue.length; i++) {
          if (index + i < length) {
            newValue[index + i] = pastedValue[i];
          }
        }

        const finalValue = newValue.join('').slice(0, length);
        onChange(finalValue);

        const nextIndex = Math.min(index + pastedValue.length, length - 1);
        if (inputRefs.current[nextIndex]) {
          inputRefs.current[nextIndex].focus();
        }

        if (finalValue.length === length && onComplete) {
          onComplete(finalValue);
        }
      } else {
        const newValue = currentValue.split('');
        newValue[index] = sanitizedValue;
        const finalValue = newValue.join('').slice(0, length);
        onChange(finalValue);

        if (sanitizedValue && index < length - 1) {
          const nextInput = inputRefs.current[index + 1];
          if (nextInput) {
            nextInput.focus();
          }
        }

        if (finalValue.length === length && onComplete) {
          onComplete(finalValue);
        }
      }
    },
    [currentValue, length, onChange, onComplete]
  );

  const handleKeyDown = useCallback(
    (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
      const { key } = event;

      if (key === 'Backspace') {
        event.preventDefault();

        if (currentValue[index]) {
          const newValue = currentValue.split('');
          newValue[index] = '';
          onChange(newValue.join(''));
        } else if (index > 0) {
          const newValue = currentValue.split('');
          newValue[index - 1] = '';
          onChange(newValue.join(''));
          inputRefs.current[index - 1]?.focus();
        }
      } else if (key === 'Delete') {
        event.preventDefault();
        const newValue = currentValue.split('');
        newValue[index] = '';
        onChange(newValue.join(''));
      } else if (key === 'ArrowLeft' && index > 0) {
        event.preventDefault();
        inputRefs.current[index - 1]?.focus();
      } else if (key === 'ArrowRight' && index < length - 1) {
        event.preventDefault();
        inputRefs.current[index + 1]?.focus();
      } else if (key === 'Tab') {
        return;
      } else if (key.length === 1 && !/[a-zA-Z0-9]/.test(key)) {
        event.preventDefault();
      }
    },
    [currentValue, length, onChange]
  );

  const handlePaste = useCallback(
    (index: number, event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const pastedData = event.clipboardData.getData('text');
      const sanitizedValue = pastedData.replace(/[^a-zA-Z0-9]/g, '');
      const newValue = currentValue.split('');

      for (let i = 0; i < sanitizedValue.length && index + i < length; i++) {
        newValue[index + i] = sanitizedValue[i];
      }

      const finalValue = newValue.join('').slice(0, length);
      onChange(finalValue);

      if (finalValue.length === length && onComplete) {
        onComplete(finalValue);
      }

      // Focus next empty input
      const nextIndex = Math.min(index + sanitizedValue.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
    },
    [currentValue, length, onChange, onComplete]
  );

  const handleFocus = useCallback((index: number) => {
    const input = inputRefs.current[index];
    if (input) {
      input.select();
    }
  }, []);

  const handleClick = useCallback((index: number) => {
    inputRefs.current[index]?.focus();
  }, []);

  const renderSeparator = (index: number) => {
    if (!separator || index === length - 1) {
      return null;
    }
    return <div className='w-2 h-0.5 bg-gray-900 mx-1' />;
  };

  const sizeClass = {
    xl: 'w-15 [&>input]:!text-[2rem] font-semibold',
    lg: 'w-12 [&>input]:!text-[1.75rem] font-semibold',
    md: 'w-10 font-semibold',
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {label && (
        <label className='text-sm font-medium text-gray-700'>{label}</label>
      )}

      <div
        className={`flex items-center justify-center gap-2 ${inputWrapperclassName}`}
        role='group'
        aria-label={ariaLabel || `OTP input with ${length} digits`}
      >
        {Array.from({ length }, (_, index) => (
          <React.Fragment key={index}>
            <div className='relative'>
              <div
                ref={el => {
                  if (el) {
                    inputRefs.current[index] = el.querySelector(
                      'input'
                    ) as HTMLInputElement;
                  }
                }}
              >
                <Input
                  value={currentValue[index] || ''}
                  onChange={e => handleInputChange(index, e.target.value)}
                  onKeyDown={e => handleKeyDown(index, e)}
                  onFocus={() => handleFocus(index)}
                  onClick={() => handleClick(index)}
                  onPaste={e => handlePaste(index, e)}
                  disabled={disabled}
                  size={size}
                  variant={variant}
                  color={color}
                  className={`${sizeClass[size]} text-center ${inputClassName}`}
                  inputClassName='text-center font-mono text-lg'
                  maxLength={1}
                  type='text'
                  autoComplete='off'
                  autoCorrect='off'
                  autoCapitalize='off'
                  spellCheck='false'
                  aria-label={`Digit ${index + 1} of ${length}`}
                />
              </div>
            </div>
            {renderSeparator(index)}
          </React.Fragment>
        ))}
      </div>

      {helperText && (
        <p className={`text-sm text-gray-400 ${helperTextClassName}`}>
          {helperText}
        </p>
      )}
    </div>
  );
};

export default OTP;
